<template>
  <div class="">
    <div
      ref="facetedSerachContainer"
      class="faceted-search"
      :class="{
        'faceted-search--disabled': disabled,
        'faceted-search--active': selectedOption,
      }"
    >
      <div class="faceted-search__content" @click="onClickFiltersOptions">
        <!-- Persistent filters -->
        <div
          v-for="(filter, index) in filters"
          :key="`${filter.field}-${filter.value}-${index}`"
          class="faceted-search__filter"
        >
          <span class="faceted-search__filter-label">
            {{ filter.label }}:
          </span>
  
          <span class="faceted-search__filter-value">
            {{ filter.value }}
          </span>
  
          <button
            type="button"
            class="faceted-search__remove text-white"
            :aria-label="`Remove ${filter.label} filter`"
            :disabled="disabled"
            @click.stop="onClickRemoveFilter(index)"
          >
            ×
          </button>
        </div>
  
        <!-- Field selector -->
        <v-select
          ref="fieldSelector"
          v-if="!selectedOption"
          v-model="selectedField"
          :options="availableOptions"
          :clearable="false"
          :searchable="true"
          :disabled="disabled"
          :placeholder="$t('unit.add_filters')"
          label="label"
          class="faceted-search__select"
          append-to-body
          :calculate-position="calculatePosition"
          @input="onFieldSelected"
          @keydown.native="onFieldSelectorKeydown"
          @search="onSearchFielSelector"
        >
          <template #option="option">
            <div class="faceted-search__option">
              <span>{{ option.label }}</span>
  
              <small v-if="option.description">
                {{ option.description }}
              </small>
            </div>
          </template>
        </v-select>
  
        <!-- Value input after selecting a field -->
        <div
          v-else
          class="faceted-search__value-container"
        >
          <span class="faceted-search__selected-label">
            {{ selectedOption.label }}:
          </span>
  
          <input
            ref="inputRef"
            v-model="searchValue"
            type="text"
            class="faceted-search__input"
            :disabled="disabled"
            @keydown="onValueKeydown"
          />
  
          <button
            type="button"
            class="faceted-search__cancel"
            aria-label="Cancel current filter"
            @click="selectedField = null; searchValue = ''"
          >
            ×
          </button>
        </div>
      </div>
  
      <button
        v-if="filters.length"
        type="button"
        class="faceted-search__clear"
        :disabled="disabled"
        @click="clearFilters"
      >
        {{ $t("risk.template.clear") }}
      </button>
    </div>
    <div
      class="hiddenSelectOptions"
      v-if="showOptions && filters.length">
      <v-select
        v-model="selectedOptions"
        :options="optionsToShow"
        :clearable="false"
        class="white-bg"
        ref="hiddenOptionsSelect"
        @input="onSelectOption"
        append-to-body
      />
    </div>
  </div>
</template>

<script>
import { watch, onMounted, onBeforeUnmount} from '@vue/composition-api';
import vSelect from 'vue-select';
import { useFacetedSearch } from '@/composables/useFacetedSearch'

export default {
  name: 'FacetedSearch',

  components: {
    vSelect,
  },

  props: {
    options: {
      type: Array,
      required: true,
      default: () => [],
    },
    value: {
      type: Array,
      default: () => [],
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    allowDuplicateFields: {
      type: Boolean,
      default: false,
    },
    showOptions: {
      type: Boolean,
      default: false
    },
    isHorizontal: {
      type: Boolean,
      default: false
    },
    optionsToShow: {
      type: Array,
      default: () => [],
    },
    itemIndex: {
      type: Number,
      default: 0
    },
    showFieldBottom: {
      type: Boolean,
      default: false
    },
    // Field a typed value falls back to when none was picked explicitly.
    defaultField: {
      type: String,
      default: null,
    },
  },

  setup(props, { emit }) {
    const {
      inputRef,
      filters,
      selectedField,
      searchValue,
      selectedOption,
      availableOptions,
      canAddFilter,
      selectedOptions,
      hiddenOptionsSelect,
      facetedSerachContainer,
      fieldSelector,
      addFilter,
      onClickRemoveFilter,
      clearFilters,
      onFieldSelected,
      onValueKeydown,
      buildFiltersArray,
      handleClickOutside,
      onSelectOption,
      onClickFiltersOptions,
      onFieldSelectorKeydown,
      onSearchFielSelector,
      calculatePosition
    } = useFacetedSearch(emit, props)

    watch(
      () => props.value,
      newValue => {
        filters.value = buildFiltersArray(newValue);
      },
      {
        deep: true,
      }
    );

    onMounted(() => {
      document.addEventListener('click', handleClickOutside);
    });

    onBeforeUnmount(() => {
      document.removeEventListener('click', handleClickOutside);
    });

    return {
      inputRef,
      filters,
      selectedField,
      searchValue,
      selectedOption,
      availableOptions,
      canAddFilter,
      selectedOptions,
      hiddenOptionsSelect,
      facetedSerachContainer,
      fieldSelector,
      addFilter,
      onClickRemoveFilter,
      clearFilters,
      onFieldSelected,
      onValueKeydown,
      buildFiltersArray,
      onSelectOption,
      onClickFiltersOptions,
      onFieldSelectorKeydown,
      onSearchFielSelector,
      calculatePosition
    };
  },
};
</script>

<style scoped lang="scss">
.faceted-search {
  display: flex;
  align-items: stretch;
  width: 100%;
  min-height: 40px;
  border: 1px solid #d8d8d8;
  border-radius: 6px;
  background: #fff;
  z-index: 10;
}

.faceted-search:focus-within {
  box-shadow: 0 0 0 1px rgb(0, 0, 0);
}

.faceted-search__content {
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  min-width: 0;
  padding: 0px 8px;
}

.faceted-search__filter {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  min-height: 25px;
  padding: 3px 7px;
  border: 1px solid #cfd3d8;
  border-radius: 4px;
  background: #f5f6f7;
  white-space: nowrap;
  background-color: var(--primary) !important;
  color: #fff
}

.faceted-search__filter-label {
  font-weight: 600;
}

.faceted-search__filter-value {
  max-width: 220px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.faceted-search__remove,
.faceted-search__cancel {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #6e6b7b;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
}

/*
 * The width is a preference, not a floor: as a min-width the field selector
 * and the value input keep their size when the toolbar around them shrinks
 * and spill out of the search box (measured: 198px of content in a 168px
 * slot, overlapping the buttons next to it).
 */
.faceted-search__select {
  flex: 1 1 190px;
  min-width: 0;
}

.faceted-search__value-container {
  display: flex;
  flex: 1 1 240px;
  align-items: center;
  min-width: 0;
}

.faceted-search__selected-label {
  margin-right: 5px;
  font-weight: 600;
  white-space: nowrap;
}

.faceted-search__input {
  flex: 1 1 120px;
  min-width: 0;
  padding: 5px 4px;
  border: 0;
  outline: 0;
  background: transparent;
}

.faceted-search__clear {
  padding: 0 14px;
  border: 0;
  border-left: 1px solid #d8d8d8;
  background: transparent;
  cursor: pointer;
}

.faceted-search--disabled {
  background: #f3f2f7;
  opacity: 0.7;
  pointer-events: none;
}

.faceted-search__option {
  display: flex;
  flex-direction: column;
}

.faceted-search__option small {
  margin-top: 2px;
  color: #8a8a8a;
}

/*
 * Remove the default vue-select border because the outer
 * component already provides the search border.
 */
.faceted-search__select ::v-deep .vs__dropdown-toggle {
  min-height: 30px;
  padding: 0;
  border: 0;
}

.faceted-search__select ::v-deep .vs__search {
  margin: 0;
  padding: 4px 2px;
}

.faceted-search__select ::v-deep .vs__selected {
  margin: 0;
  padding: 0;
}

.faceted-search__select ::v-deep .vs__actions {
  padding-top: 0;
  padding-bottom: 0;
}
.dark-layout {
  .faceted-search {
    background-color: var(--second-dark) !important;
  }
  .faceted-search__input, .faceted-search__cancel, .faceted-search__clear {
    color: #b4b7bd;
  }
}
.hiddenSelectOptions ::v-deep .vs__dropdown-toggle {
  border: none;
  background: transparent;
  pointer-events: none;
  top: -20px;
  position: relative;
}
.hiddenSelectOptions ::v-deep .vs__actions {
  display: none;
}
/* Don't display selected value */
.hiddenSelectOptions ::v-deep .vs__selected {
  display: none;
}
.hiddenSelectOptions ::v-deep .vs__dropdown-menu{
  top: 10px !important;
}

</style>